import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, PlugsConnected, Warning } from "@phosphor-icons/react";
import api from "@/lib/api";
import { BuyerRefreshButton } from "@/components/buyer/BuyerRefreshButton";

function BuyerLine({ b }) {
  const p = `setup-check-${b.slug}`;
  return (
    <li className="flex flex-wrap items-start gap-2 border-t border-zinc-200 py-2 text-[11px]" data-testid={p}>
      {b.issues.length ? <Warning size={14} weight="bold" className="mt-0.5 shrink-0 text-amber-700" />
        : <CheckCircle size={14} weight="bold" className="mt-0.5 shrink-0 text-emerald-700" />}
      <div className="min-w-0 flex-1">
        <div><b>{b.firm_name}</b> <span className="font-mono text-[10px] text-zinc-500">/lp/{b.slug} · {b.status}</span></div>
        {b.issues.length ? (
          <ul className="list-disc pl-4 text-zinc-700" data-testid={`${p}-issues`}>{b.issues.map((i) => <li key={i}>{i}</li>)}</ul>
        ) : <div className="text-emerald-800">All set</div>}
      </div>
      <Link to={b.org_id ? `/clients/${b.org_id}/manage` : "/admin/landing-pages"} className="text-[10px] uppercase tracking-widest underline" data-testid={`${p}-fix`}>
        {b.org_id ? "Open buyer" : "Landing pages"}
      </Link>
    </li>
  );
}

// Every real buyer, and anything missing that would stop leads, their dashboard, requests or update emails.
export function BuyerSetupCheck() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(() => window.location.hash === "#setup-check");
  const load = () => api.get("/admin/buyer-setup-check").then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => {
    load();
  }, []);
  useEffect(() => {
    if (data && open && window.location.hash === "#setup-check") {
      document.getElementById("setup-check")?.scrollIntoView({ behavior: "smooth" });
    }
  }, [data, open]);
  if (!data) return null;
  const ok = !data.needs_attention;
  return (
    <section id="setup-check" className="mt-8 border-2 border-black bg-white" data-testid="buyer-setup-check">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full flex-wrap items-center gap-2 p-3 text-left" data-testid="buyer-setup-check-toggle">
        <PlugsConnected size={16} weight="bold" />
        <span className="font-chivo font-black uppercase tracking-tighter">Buyer setup check</span>
        <span className={`ml-auto px-2 py-0.5 text-[10px] uppercase tracking-widest ${ok ? "bg-emerald-600 text-white" : "bg-amber-400"}`} data-testid="buyer-setup-check-summary">
          {ok ? `All ${data.total} buyers connected` : `${data.needs_attention} of ${data.total} need attention`}
        </span>
      </button>
      {open && (
        <div className="border-t border-black px-3 pb-2 pt-2">
          <p className="mb-1 text-[11px] text-zinc-600">
            For requests, Edit details and update emails to work, each buyer page needs a delivery that is linked to their client company, a main contact email and a buyer login. Run this on your live site after you publish.
          </p>
          <BuyerRefreshButton onDone={load} className="mb-2" />
          <ul data-testid="buyer-setup-check-list">{data.buyers.map((b) => <BuyerLine key={b.page_id} b={b} />)}</ul>
          {!data.buyers.length && <div className="py-3 text-[11px] text-zinc-500">No buyers yet.</div>}
        </div>
      )}
    </section>
  );
}
